/* Homepage layout measurements only; the Enterprise slideshow controller is unchanged. */
(() => {
  'use strict';
  function positionOffset(token, freeSpace, axis) {
    const keywords = axis === 'x' ? { left: 0, center: 50, right: 100 } : { top: 0, center: 50, bottom: 100 };
    if (Object.hasOwn(keywords, token)) return freeSpace * keywords[token] / 100;
    if (/^-?\d+(?:\.\d+)?%$/.test(token)) return freeSpace * parseFloat(token) / 100;
    if (/^-?\d+(?:\.\d+)?px$/.test(token)) return parseFloat(token);
    return null;
  }
  function calculateKipodOffset({ width, height, naturalWidth, naturalHeight, objectFit, objectPosition }) {
    if (![width, height, naturalWidth, naturalHeight].every(n => Number.isFinite(n) && n > 0)) return null;
    if (Math.abs(naturalWidth / naturalHeight - 2322 / 677) > 0.02) return null;
    if (!['contain', 'cover'].includes(objectFit)) return null;
    const tokens = objectPosition.trim().split(/\s+/);
    if (tokens.length !== 2) return null;
    const scale = (objectFit === 'cover' ? Math.max : Math.min)(width / naturalWidth, height / naturalHeight);
    const renderedWidth = naturalWidth * scale;
    const renderedHeight = naturalHeight * scale;
    if (positionOffset(tokens[0], width - renderedWidth, 'x') === null) return null;
    const baselineY = positionOffset(tokens[1], height - renderedHeight, 'y');
    if (baselineY === null) return null;
    const blankBorder = renderedHeight * 37 / 677;
    if (baselineY + blankBorder <= 0.25) return null; // Native cover/focal positioning already hides it.
    return { x: tokens[0], y: -blankBorder };
  }
  if (typeof module !== 'undefined' && module.exports) module.exports = { calculateKipodOffset };
  if (typeof window === 'undefined' || typeof document === 'undefined') return;
  if (window.fabianHomeLayout) { window.fabianHomeLayout.init(document); return; }

  const states = new Map();
  const desktop = window.matchMedia('(min-width: 769px)');

  function restorePosition(state) {
    if (!state.image) return;
    if (state.originalPosition) state.image.style.setProperty('object-position', state.originalPosition, state.originalPriority);
    else state.image.style.removeProperty('object-position');
  }

  function update(state) {
    if (!state.root.isConnected) return;
    const activeMedia = state.root.querySelector('.slideshow__slide.is-active .image-banner__image');
    const anchor = activeMedia || state.viewport;
    const box = anchor.getBoundingClientRect();
    const rootBox = state.root.getBoundingClientRect();
    if (box.height > 0) state.root.style.setProperty('--fabian-home-arrow-top', (box.top - rootBox.top + box.height / 2) + 'px');

    restorePosition(state);
    const image = state.image;
    if (!image || !desktop.matches) { state.root.dataset.fabianKipodTrim = 'inactive'; return; }
    if (!image.complete || !image.naturalWidth) { state.root.dataset.fabianKipodTrim = 'pending-image'; return; }
    let filename;
    try { filename = decodeURIComponent(new URL(image.currentSrc || image.src, document.baseURI).pathname.split('/').pop()); }
    catch { state.root.dataset.fabianKipodTrim = 'unsupported-source'; return; }
    if (filename !== 'kipod-new.webp') { state.root.dataset.fabianKipodTrim = 'unsupported-source'; return; }

    const computed = window.getComputedStyle(image);
    const correction = calculateKipodOffset({
      width: image.clientWidth,
      height: image.clientHeight,
      naturalWidth: image.naturalWidth,
      naturalHeight: image.naturalHeight,
      objectFit: computed.objectFit,
      objectPosition: computed.objectPosition
    });
    if (!correction) { state.root.dataset.fabianKipodTrim = 'not-needed-or-unsupported'; return; }
    image.style.setProperty('object-position', correction.x + ' ' + correction.y + 'px', state.originalPriority);
    state.root.dataset.fabianKipodTrim = 'applied';
  }

  function cleanup(root) {
    const state = states.get(root);
    if (!state) return;
    restorePosition(state);
    if (state.frame) window.cancelAnimationFrame(state.frame);
    state.resizeObserver?.disconnect();
    state.mutationObserver?.disconnect();
    root.removeEventListener('load', state.schedule, true);
    window.removeEventListener('resize', state.schedule);
    desktop.removeEventListener('change', state.schedule);
    root.style.removeProperty('--fabian-home-arrow-top');
    delete root.dataset.fabianKipodTrim;
    states.delete(root);
  }

  function init(scope) {
    for (const root of states.keys()) if (!root.isConnected) cleanup(root);
    const roots = [...scope.querySelectorAll('[data-fabian-home-hero="true"]')];
    if (scope.matches?.('[data-fabian-home-hero="true"]')) roots.unshift(scope);
    for (const root of roots) {
      if (states.has(root)) continue;
      const viewport = root.querySelector('.slideshow');
      if (!viewport) continue;
      const image = root.querySelector('[data-fabian-slide-id="slide_XaVefP"] img');
      const state = {
        root, viewport, image,
        originalPosition: image?.style.getPropertyValue('object-position') || '',
        originalPriority: image?.style.getPropertyPriority('object-position') || '',
        frame: 0
      };
      state.schedule = () => {
        if (state.frame) window.cancelAnimationFrame(state.frame);
        state.frame = window.requestAnimationFrame(() => { state.frame = 0; update(state); });
      };
      states.set(root, state);
      if (window.ResizeObserver) {
        state.resizeObserver = new window.ResizeObserver(state.schedule);
        state.resizeObserver.observe(viewport);
      }
      if (window.MutationObserver) {
        state.mutationObserver = new window.MutationObserver(state.schedule);
        state.mutationObserver.observe(viewport, { subtree: true, attributes: true, attributeFilter: ['class'] });
      }
      root.addEventListener('load', state.schedule, true);
      window.addEventListener('resize', state.schedule);
      desktop.addEventListener('change', state.schedule);
      state.schedule();
    }
  }

  window.fabianHomeLayout = { init };
  document.addEventListener('shopify:section:load', event => init(event.target));
  document.addEventListener('shopify:section:unload', event => {
    for (const root of states.keys()) if (root === event.target || event.target.contains(root)) cleanup(root);
  });
  init(document);
})();

